import { useEffect, useRef, useState } from 'react';
import salesBotAvatar from '@/assets/images/sales_bot_avatar.jpg';
import useSalesBotStore from '@/stores/SalesBotStore';
import { PhoneIcon, RefreshCw, VideoIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { cn } from '@/lib/utils';
import { sendDemoPredict } from '@/hooks/sales-bot';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';

import { Button } from '../ui/button';

const AutopilotDemo = () => {
  const {
    messages,
    addMessage,
    resetMessages,
    conversationId,
    setConversationId,
  } = useSalesBotStore(
    useShallow((state) => ({
      messages: state.messages,
      addMessage: state.addMessage,
      resetMessages: state.resetMessages,
      conversationId: state.conversationId,
      setConversationId: state.setConversationId,
    }))
  );
  const [inputValue, setInputValue] = useState('');
  const [isBotTyping, setIsBotTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const handleResetConversation = () => {
    resetMessages();
    setInputValue('');
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
          textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
      });
    }
  }, [inputValue]);

  const handleSendMessage = async () => {
    if (inputValue.trim() === '') return;

    const userMessage = {
      id: messages.length + 1 + Math.random(),
      text: inputValue,
      type: 'sent',
    };
    addMessage(userMessage);
    const currentMessageText = inputValue;
    setInputValue('');
    setIsBotTyping(true);

    try {
      const response = await sendDemoPredict(
        currentMessageText,
        conversationId
      );
      if (response) {
        if (response.conversation_id && !conversationId) {
          setConversationId(response.conversation_id);
        }
        if (response.text) {
          const messageLines = response.text.split('\n');
          messageLines.forEach((line, index) => {
            if (line.trim() !== '') {
              const botMessage = {
                id: `${
                  messages.length
                }-${Date.now()}-${index}-${Math.random()}`,
                text: line,
                type: 'received',
              };
              addMessage(botMessage);
            }
          });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send message');
    } finally {
      setIsBotTyping(false);
    }
  };

  return (
    <div className='flex w-full flex-col items-center justify-center overflow-y-auto p-4 lg:w-1/2 lg:p-6'>
      <div className='flex w-full max-w-[380px] flex-col items-center'>
        <div
          className='relative mx-auto w-full rounded-xl border-[8px] border-gray-800 bg-gray-800 shadow-xl dark:border-gray-800'
          style={{
            height: 'clamp(500px, min(750px, calc(90vh - 12rem)), 750px)',
            aspectRatio: '380 / 750',
          }}
        >
          <div className='absolute -start-[10px] top-[72px] h-[32px] w-[3px] rounded-s-lg bg-gray-800'></div>
          <div className='absolute -start-[10px] top-[124px] h-[46px] w-[3px] rounded-s-lg bg-gray-800'></div>
          <div className='absolute -start-[10px] top-[178px] h-[46px] w-[3px] rounded-s-lg bg-gray-800'></div>
          <div className='absolute -end-[10px] top-[142px] h-[64px] w-[3px] rounded-e-lg bg-gray-800'></div>
          <div className='absolute inset-[8px] flex overflow-hidden rounded-xl bg-white dark:bg-gray-900'>
            <div className='flex h-full w-full flex-col bg-background dark:bg-black'>
              {messages.length > 0 && (
                <div className='flex items-center justify-between border-b p-3'>
                  <div className='flex items-center gap-x-2'>
                    <Avatar className='mr-1 h-8 w-8'>
                      <AvatarImage
                        src={salesBotAvatar}
                        alt='Revio Autopilot Avatar'
                      />
                      <AvatarFallback className='bg-gray-400 dark:bg-gray-600'>
                        AL
                      </AvatarFallback>
                    </Avatar>
                    <div className='flex flex-col'>
                      <span className='font-semibold text-gray-800 dark:text-white'>
                        Ashley Lemieux
                      </span>
                      <span className='text-sm text-gray-500 dark:text-gray-400'>
                        ashley_lemieux
                      </span>
                    </div>
                  </div>
                  <div className='flex items-center gap-x-4'>
                    <PhoneIcon className='h-5 w-5' />
                    <VideoIcon className='h-6 w-6' />
                  </div>
                </div>
              )}
              <ScrollArea className='flex-1'>
                {messages.length === 0 ? (
                  <div className='flex h-full min-h-full items-center justify-center p-4'>
                    <p className='text-center text-sm text-gray-500 dark:text-gray-400'>
                      Send a message to start the demo!
                    </p>
                  </div>
                ) : (
                  <div className='w-full space-y-3 p-3'>
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={cn(
                          'flex items-end gap-x-2',
                          message.type === 'sent'
                            ? 'justify-end'
                            : 'justify-start'
                        )}
                      >
                        <div
                          className={cn(
                            'max-w-[75%] whitespace-pre-wrap break-words rounded-t-xl px-3 py-2 text-sm',
                            message.type === 'sent'
                              ? 'rounded-bl-xl bg-gradient-to-r from-[#405DE6] via-[#5851DB] to-[#833AB4] text-white'
                              : 'rounded-br-xl bg-[#F0F2F5] text-black dark:bg-[#262626] dark:text-white'
                          )}
                        >
                          {message.text}
                        </div>
                      </div>
                    ))}
                    {isBotTyping && (
                      <div className='flex items-end justify-start gap-x-2'>
                        <div className='max-w-[75%] whitespace-pre-wrap break-words rounded-t-xl rounded-br-xl bg-[#F0F2F5] px-3 py-2 text-sm text-black opacity-70 dark:bg-[#262626] dark:text-white'>
                          <div className='animate-bounce'>. . .</div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>
              <div className='p-1'>
                <div className='flex max-h-32 items-end rounded-md border p-1'>
                  <textarea
                    ref={textareaRef}
                    rows={1}
                    value={inputValue}
                    onInput={(e) => {
                      const target = e.target;
                      target.style.height = 'auto';
                      target.style.height = `${target.scrollHeight}px`;
                    }}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.shiftKey) {
                        // Default behavior (newline) is allowed
                      } else if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder='Type a message...'
                    className='custom-scrollbar max-h-28 flex-1 resize-none overflow-y-auto bg-transparent p-1 text-sm text-gray-800 placeholder-gray-500 focus:outline-none dark:text-white dark:placeholder-gray-400'
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={inputValue.trim() === ''}
                    className='p-1 text-sm font-semibold text-blue-600 hover:text-blue-700 disabled:text-gray-400 dark:text-blue-400 dark:hover:text-blue-300 dark:disabled:text-gray-500'
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='mt-4 h-[40px]'>
          <Button
            onClick={handleResetConversation}
            disabled={messages.length === 0}
            variant='outline'
            className='h-full'
          >
            <RefreshCw className='mr-2 h-4 w-4' />
            <span>Reset Conversation</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AutopilotDemo;
